/**
 * Coalesce concurrent identical GETs so mount bursts / duplicate components
 * share one network round-trip (Phase 1.2 request dedupe).
 *
 * Key is method + url + serialized params from the caller's config (pre-interceptor).
 * Axios request interceptors still run for the single outbound request; callers
 * awaiting the shared promise all receive the same response.
 *
 * Skipped when: non-JSON response types, AbortSignal present, or opt-out header.
 */

const inflight = new Map<string, Promise<unknown>>();

/** Set on request `headers` to force a separate in-flight GET (rare). */
export const DEDUPE_HEADER = 'X-Edgentik-Skip-Inflight-Dedupe';

function stableSerializeValue(v: unknown): string {
  if (v === undefined) return '';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function stableSerializeParams(params: unknown): string {
  if (params == null || params === '') return '';
  if (params instanceof URLSearchParams) {
    const keySet = new Set<string>();
    Array.from(params.keys()).forEach((k) => keySet.add(k));
    const keys = Array.from(keySet).sort();
    return keys.map((k) => `${k}=${params.getAll(k).join(',')}`).join('&');
  }
  if (typeof params !== 'object') return stableSerializeValue(params);
  const obj = params as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return keys.map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(stableSerializeValue(obj[k]))}`).join('&');
}

export function buildHttpGetDedupeKey(url: string, config?: { params?: unknown } | null): string {
  const q = stableSerializeParams(config?.params);
  return q ? `GET ${url}?${q}` : `GET ${url}`;
}

export function shouldDedupeAxiosGet(config?: { signal?: unknown; responseType?: string; headers?: unknown } | null): boolean {
  if (!config) return true;
  if (config.signal) return false;
  const rt = config.responseType;
  if (rt && rt !== 'json') return false;
  const h: any = config.headers;
  if (h) {
    const v = h[DEDUPE_HEADER] ?? h['x-edgentik-skip-inflight-dedupe'];
    if (v === '1' || v === 'true' || v === true) return false;
  }
  return true;
}

export function runDeduped<T>(key: string, factory: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const p = factory().finally(() => {
    if (inflight.get(key) === p) inflight.delete(key);
  });
  inflight.set(key, p);
  return p as Promise<T>;
}
